import { useEffect, useRef, useState } from "react";
import { Button, Tabs, Tab, FormCheck } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import { SimpleFormField } from "../components/SimpleFormField";
import { getDB } from "../util/db";
import { arrayElementMove, setNavAdvisoryLockout } from "../util/utils";
import { useProfile } from "../components/ProfileContext";
import { LoadingComponent } from "../components/LoadingComponent";
import { Trash, ArrowDown, ArrowUp } from "react-bootstrap-icons";
import { Survey } from "../models/Survey";
import { QuestionEditorFormStates } from "../forms/QuestionEditorFormStates";

export function SurveyPage(props) {
	setNavAdvisoryLockout(true);

	let params = useParams();
	let navigate = useNavigate();
	let surveyId = params.surveyId;
	let surveyIsNew = surveyId == "new";

	let [survey, setSurvey] = useState(surveyIsNew ? {surveyComponentTypes:[],surveyQuestions:[]} : null);
	let [ctypes, setCtypes] = useState(null);
	let globalCheckRef = useRef();
	let scrollViewRef = useRef();
	let [profile] = useProfile();
	// Number of selected machine component types
	// Will be used for positioning purposes
	let numComps = survey?.surveyComponentTypes.length;

	// Set of selected component types so that their buttons don't show up on the botton of the Machine Components tab
	let selectedCTypes = new Set(survey?.surveyComponentTypes.map( x => {return x.componentTypeId}));

	let isLoading = (survey == null) || (ctypes == null);

	useEffect(async () => {
		if(isLoading) {
			let db = await getDB();
			if(!surveyIsNew) {
				let loadedSurvey = await db.get("Surveys", surveyId);
				if(loadedSurvey == null) {
					navigate("/surveys"); // Can't find survey
				}
				// Sort the component types by the position the user wanted when they saved the survey
				loadedSurvey.surveyComponentTypes = loadedSurvey.surveyComponentTypes.toSorted((a, b) => a.position - b.position);
				
				setSurvey(loadedSurvey);
			}
			let loadedCtypes = await db.getAll("ComponentTypes");
			setCtypes(loadedCtypes);
		}
	});


	let cancelAction = () => {
		if(confirm("Are you sure you want to discard changes?")) {
			navigate("/surveys");
		}
	};

	let saveAction = async () => {
		if(!survey.name?.trim()) {
			alert("Survey must have a name!");
			return;
		}

		// Not sure if I need this but we'll leave it here just in case
		// if(!survey.surveyComponentTypes || survey.surveyComponentTypes.length == 0){
		// 	alert("Component Type(s) must be selected!");
		// 	return;
		// }

		for(const question of survey.surveyQuestions){
			// Check for blank entries
			if(question.question.newSection && !question.question.question) {
				alert("All new sections must have a name!");
				return;
			}
			if(!question.question.newSection){
				if (!question.question.name){
					alert("All Questions must have short names!");
						return;
				} else if (!question.question.question){
					alert("All Questions must have names!");
						return;
				}
			}
			
			// If the question is not a new section, it should have comments only, resolvable, or skippable selected
			if(!question.question.newSection && !question.question.commentsOnly && !question.question.resolvable && !question.question.skippable){
				alert(`question ${question.question.name} must have a selected response!`);
				return
			}
		}
		
		if(!survey?.surveyComponentTypes || survey.surveyComponentTypes.length == 0) {
			alert("Survey must have component type(s)");
			return;
		}

		try {
			if(profile.admin) {
				survey.global = globalCheckRef.current.checked;
			}
			let newSurvey = await Survey.adminUpdateSurvey(survey, profile);
			navigate("/surveys");	
		} catch(err) {
			alert(`Error updating survey: ${err}`);
		}
	};

	let addComponentType = (ct) => {
		if(survey.surveyComponentTypes.filter(sct => sct.componentTypeId == ct.id).length > 0) {
			alert("Component already exists");
			return;
		}

		
		setSurvey({
			...survey,
			surveyComponentTypes:
			[
				...survey.surveyComponentTypes,
				{
					"componentTypeId": ct.id,
					"componentType": ct,
					"position": numComps,
				}
			]
			
		});
	}

	let componentMoveAction = (sct, offset) => () => {

		sct = arrayElementMove(survey.surveyComponentTypes, sct, offset);
		// Reset the positioning for the components
		sct = sct.map( (x, idx) => {
			return {
				...x,
				"position": idx,
			}
		})
		
		setSurvey({
			...survey,
			surveyComponentTypes: sct
		});
	};

	let removeComponentAction = (sct) => () => {
		let index = survey.surveyComponentTypes.indexOf(sct);

		let ct_list = survey.surveyComponentTypes;
		// Remove the component
		ct_list = ct_list.filter(mysct => (sct != mysct));
		// Reindex the component types
		ct_list = ct_list.map( (sct, idx) => {
			if(idx < index){
				return sct
			} else {
				return {
					...sct,
					"position": idx
				}
			}
		} )

		setSurvey({
			...survey,
			surveyComponentTypes: ct_list,
		});
	};
	let tabContentStyle = "p-4 bg-light mb-4";
	let deleteSurveyAction = async () => {
		if(!confirm("Are you sure you want to delete this survey?")) {
			return;
		}

		try {
			await Survey.adminDeleteSurvey(survey, profile);
			navigate("/surveys")
		} catch(err) {
			alert(`Error deleting survey: ${err}`);
		}
	};

	if(isLoading) {
		return <LoadingComponent />;
	}
	return (
		<div className="generalPageContainer">
			<h1>
				Edit Survey
				<Button variant="primary" className="float-end mt-2" onClick={saveAction}>Save</Button>
				<Button variant="danger" className="float-end m-2" onClick={deleteSurveyAction}>Delete</Button>
				<Button variant="secondary" className="float-end m-2" onClick={cancelAction}>Cancel</Button>
			</h1>

			<Tabs className="mt-4 tabContainer">
				<Tab eventKey="basic" title="Basic Information">
					<div className={tabContentStyle}>
					<SimpleFormField label="Survey Name" bindObject={survey} bindAttribute="name" />
					<SimpleFormField label="Category Name" bindObject={survey} bindAttribute="categoryName" />
					{profile.admin && <FormCheck ref={globalCheckRef} type="checkbox" label="Global" defaultChecked={survey.global} className="mt-2" />}
					<SimpleFormField label="Position" bindObject={survey} bindAttribute="position" />
					{profile.admin && <SimpleFormField label="Icon Name" bindObject={survey} bindAttribute="icon" />}
					</div>
				</Tab>
				<Tab eventKey="components" title="Machine Components">
					<div className={tabContentStyle}>
						<table className="table">
							<thead>
								<tr>
									<th>Name</th><th>Serial #</th><th>Model #</th><th></th>
								</tr>
							</thead>
							<tbody>
								{survey.surveyComponentTypes.map(sct => {
									let ct = sct.componentType;
									return <tr key={ct.id}>
										<td>{ct.name}</td>
										<td>{ct.hasSerialNumber ? "Y" : "N"}</td>
										<td>{ct.hasModelNumber ? "Y" : "N"}</td>
										<td>
											<ArrowDown className="mx-2" onClick={componentMoveAction(sct, 1)} />
											<ArrowUp className="mx-2" onClick={componentMoveAction(sct, -1)}/>
											<Trash className="mx-4" onClick={removeComponentAction(sct)} />
										</td>
									</tr>;
								})}
							</tbody>
						</table>

						<hr />

						{ctypes.filter( elt => { return !selectedCTypes.has(elt.id) } ).map(ctype => <Button key={ctype.id} onClick={() => {addComponentType(ctype); scrollViewRef.current.scrollIntoView()}} className="m-2">{ctype.name}</Button>)}
						<div ref={scrollViewRef}></div>
					</div>
				</Tab>
				<Tab eventKey="questions" title="Survey Questions">
				<div className={tabContentStyle}>

					<QuestionEditorFormStates state={survey} setState={setSurvey} questionsAttribute="surveyQuestions" sectionable={true}/>
					</div>
				</Tab>
			</Tabs>
		</div>
	)
}